import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

// Every variant carries a 1px border so the box-model height matches across
// filled and outlined buttons. Filled variants use a transparent border;
// outlined variants override the color. Without this, ghost buttons rendered
// a pixel shorter than filled siblings at sibling-comparison distance.
//
// Variant-by-context convention (do not break without a UX reason):
//   - `primary` — the dominant CTA on a page or in a dialog. Also the right
//     pick for "Add <Entity>" / "New <Entity>" at the *top* of a tab or
//     section, where the button creates a top-level row in that section
//     (Add Authorization, Add Entry, Add a card, New claim).
//   - `ghost` — inline sub-item adders inside dense editors (Add step,
//     Add block, Add choice). A primary blue/dark button shouts inside a
//     nested list of inputs; ghost keeps the form's visual weight on the
//     fields. Use `size="xs"` to match the surrounding controls.
//   - `success` — reserved for explicit positive confirmations (save +
//     close, mark complete). Don't use as a generic CTA; it reads as
//     "irreversible good outcome."
//   - `danger` — destructive confirmations (delete, void, cancel claim).
//   - `link` — text-style navigation inside prose; not for actions.
const buttonStyles = cva(
    'inline-flex items-center justify-center gap-1.5 font-medium rounded-md border border-transparent transition-colors duration-[var(--duration-base)] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap',
    {
        variants: {
            variant: {
                primary: 'bg-text text-text-inverse hover:bg-[#333]',
                success: 'bg-accent-dark text-text font-bold hover:bg-[#5a8a6a]',
                danger: 'bg-error-soft text-error border-[rgb(192_57_43_/_0.25)] hover:bg-[rgb(192_57_43_/_0.14)]',
                ghost: 'bg-surface text-text border-border hover:bg-surface-muted',
                link: 'border-transparent text-text underline-offset-4 hover:underline px-0',
            },
            size: {
                xs: 'text-[11px] px-2 py-0.5 h-6',
                sm: 'text-xs px-2.5 py-1 h-7',
                md: 'text-sm px-4 py-1.5 h-8',
                lg: 'text-sm px-5 py-2 h-10',
            },
        },
        defaultVariants: { variant: 'primary', size: 'md' },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
    asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                ref={ref}
                className={cn(buttonStyles({ variant, size }), className)}
                {...props}
            />
        );
    },
);
Button.displayName = 'Button';
